/**
 * Discriminated-union parser for the agent's `confirmation_request` frame
 * (plan 05 B3, 2026-09-29). Pure; no React.
 *
 * Frames are keyed by `(tool, schema_version)`. Each known pair maps to one
 * card kind with a strict proposal model mirrored from the agent's exported
 * JSON Schema (vendored at src/schema/confirmation_request.schema.json; the
 * drift test in confirmationRequest.schema.test.js fails when they diverge).
 *
 * Outcomes:
 *  - known pair + proposal that fits the model exactly -> that card kind.
 *  - legacy frame (no `schema_version`, no `proposal`) -> incident v1 via the
 *    pre-B3 lenient rules on `proposed`, kept for one release while old
 *    agents are still deployed.
 *  - versioned frame whose pair is unknown, or whose proposal fails the model
 *    -> 'unsupported': rendered as "not submitted", never confirmable, never
 *    silently dropped.
 *  - no usable `id` (or a malformed legacy frame) -> null; the caller drops it.
 */

export const CARD_KIND_INCIDENT_REPORT = 'incident_report';
export const CARD_KIND_UNSUPPORTED = 'unsupported';

/** Longest tool name kept on (and shown by) an unsupported card. */
export const TOOL_NAME_MAX_LENGTH = 64;

/**
 * Field model for `incident_create_or_update` v1: key -> accepted JSON types
 * plus the bounds that apply to the non-null value (JSON Schema keywords,
 * same names). Every key is required (optional ones arrive as null). Mirrors
 * the schema's `$defs.IncidentReportProposal`, bounds included (the agent's
 * registry gate enforces the same; review B3 F1/F3, 2026-09-29).
 */
export const INCIDENT_REPORT_FIELDS = Object.freeze({
  title: Object.freeze({ types: ['string'], minLength: 1, maxLength: 256 }),
  category: Object.freeze({ types: ['string'], minLength: 1, maxLength: 100 }),
  latitude: Object.freeze({ types: ['number'], minimum: -90, maximum: 90 }),
  longitude: Object.freeze({ types: ['number'], minimum: -180, maximum: 180 }),
  description: Object.freeze({ types: ['string', 'null'], maxLength: 2000 }),
  location: Object.freeze({ types: ['string', 'null'], maxLength: 500 }),
  certainty_radius_meters: Object.freeze({ types: ['number', 'null'], minimum: 0, maximum: 1000000 }),
});

/** Constraint keywords this parser enforces (the drift test rejects others). */
export const SUPPORTED_BOUND_KEYWORDS = Object.freeze(['minLength', 'maxLength', 'minimum', 'maximum']);

/**
 * Registry of renderable `(tool, schema_version)` pairs. `schemaDef` names the
 * proposal model in the vendored schema's `$defs` (used by the drift test).
 */
export const CONFIRMATION_CARD_TYPES = Object.freeze([
  Object.freeze({
    tool: 'incident_create_or_update',
    schemaVersion: 1,
    cardKind: CARD_KIND_INCIDENT_REPORT,
    schemaDef: 'IncidentReportProposal',
    fields: INCIDENT_REPORT_FIELDS,
  }),
]);

function isPlainObject(v) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function jsonTypeOf(v) {
  if (v === null) return 'null';
  if (typeof v === 'number') return Number.isFinite(v) ? 'number' : 'non-finite';
  return typeof v;
}

const LEGACY_INCIDENT_TOOL = 'incident_create_or_update';

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * JSON Schema string length counts code points (as the agent's Python `len`
 * does), not UTF-16 units, so an emoji is one character on both sides.
 */
function codePointLength(str) {
  return Array.from(str).length;
}

function withinBounds(spec, value) {
  if (typeof value === 'string') {
    // Bidi controls could make a card read differently from what is filed
    // (review B3, 2026-09-29). Strict registry parser only.
    if (BIDI_CONTROLS_RE.test(value)) return false;
    const len = codePointLength(value);
    if (spec.minLength !== undefined && len < spec.minLength) return false;
    if (spec.maxLength !== undefined && len > spec.maxLength) return false;
  } else if (typeof value === 'number') {
    if (spec.minimum !== undefined && value < spec.minimum) return false;
    if (spec.maximum !== undefined && value > spec.maximum) return false;
  }
  return true;
}

/**
 * Strict fit: exactly the model's keys (no extras, none missing), each value
 * one of its accepted types, numbers finite, non-null values within bounds.
 * Returns a fresh copy or null.
 */
export function parseStrictProposal(fields, proposal) {
  if (!isPlainObject(proposal)) return null;
  const expected = Object.keys(fields);
  const actual = Object.keys(proposal);
  if (actual.length !== expected.length) return null;
  const out = {};
  for (const key of expected) {
    if (!has(proposal, key)) return null;
    const value = proposal[key];
    const spec = fields[key];
    if (!spec.types.includes(jsonTypeOf(value))) return null;
    if (!withinBounds(spec, value)) return null;
    out[key] = value;
  }
  return out;
}

/**
 * Pre-B3 rules for an incident `proposed` object (unchanged behaviour):
 * title/category strings, finite lat/lon, optional text/radius may be
 * missing or null, extra keys ignored. Also used to vet restored cards.
 * Returns only the rendered fields (never the whole object — it is persisted
 * to localStorage; 2026-09-25 pass 20), or null.
 */
export function parseLegacyIncidentProposal(p) {
  const finite = (v) => typeof v === 'number' && Number.isFinite(v);
  const optionalText = (v) => v === null || v === undefined || typeof v === 'string';
  if (
    !p
    || typeof p !== 'object'
    || typeof p.title !== 'string'
    || typeof p.category !== 'string'
    || !finite(p.latitude)
    || !finite(p.longitude)
    || !optionalText(p.description)
    || !optionalText(p.location)
    || !(p.certainty_radius_meters === null
      || p.certainty_radius_meters === undefined
      || finite(p.certainty_radius_meters))
  ) {
    return null;
  }
  return {
    title: p.title,
    category: p.category,
    latitude: p.latitude,
    longitude: p.longitude,
    description: p.description ?? null,
    location: p.location ?? null,
    certainty_radius_meters: p.certainty_radius_meters ?? null,
  };
}

/** Bidi embedding/override (U+202A-202E) and isolate (U+2066-2069) controls. */
const BIDI_CONTROLS_RE = /[\u202A-\u202E\u2066-\u2069]/u;
const BIDI_CONTROLS_GLOBAL_RE = /[\u202A-\u202E\u2066-\u2069]/gu;
const CONTROL_FORMAT_GLOBAL_RE = /[\p{Cc}\p{Cf}]/gu;

/** Label shown when a tool name is present but nothing printable remains. */
export const UNNAMED_TOOL_LABEL = 'unnamed action';

/**
 * Plain-text tool label: control, format and bidi characters stripped, then
 * capped by code points (review B3 F5, 2026-09-29; matches v2). Returns null
 * when the tool is absent or not a string, and UNNAMED_TOOL_LABEL when
 * nothing printable remains.
 */
export function capToolName(tool) {
  if (typeof tool !== 'string' || !tool) return null;
  const cleaned = Array.from(
    tool.replace(BIDI_CONTROLS_GLOBAL_RE, '').replace(CONTROL_FORMAT_GLOBAL_RE, ''),
  );
  if (cleaned.length === 0) return UNNAMED_TOOL_LABEL;
  return cleaned.length > TOOL_NAME_MAX_LENGTH
    ? `${cleaned.slice(0, TOOL_NAME_MAX_LENGTH).join('')}…`
    : cleaned.join('');
}

function findCardType(tool, schemaVersion) {
  return CONFIRMATION_CARD_TYPES.find(
    (t) => t.tool === tool && t.schemaVersion === schemaVersion,
  ) || null;
}

/**
 * @param {object} data - Parsed WebSocket payload (hostile until proven otherwise).
 * @returns {null | {cardKind: string, confirmationId: string, tool: string|null,
 *   schemaVersion: number|null, proposed?: object}}
 */
export function parseConfirmationRequest(data) {
  if (!isPlainObject(data) || typeof data.id !== 'string' || !data.id) return null;
  const confirmationId = data.id;
  const isVersioned = (has(data, 'schema_version') && data.schema_version !== undefined)
    || (has(data, 'proposal') && data.proposal !== undefined);

  if (!isVersioned) {
    // Legacy frame from a pre-registry agent (one-release compatibility).
    // 2026-09-29 (plan 05 B3): remove one release after langchain-agent #41
    // ships (the agent then always sends schema_version).
    // Those agents only ever gated the incident tool: a legacy frame naming
    // another tool is unsupported (review B3 F2, aligned with v2); a missing
    // or non-string tool keeps the pre-B3 rules.
    if (typeof data.tool === 'string' && data.tool !== LEGACY_INCIDENT_TOOL) {
      return {
        cardKind: CARD_KIND_UNSUPPORTED,
        confirmationId,
        tool: capToolName(data.tool),
        schemaVersion: null,
      };
    }
    const proposed = parseLegacyIncidentProposal(data.proposed);
    if (!proposed) return null;
    return {
      cardKind: CARD_KIND_INCIDENT_REPORT,
      confirmationId,
      tool: typeof data.tool === 'string' ? data.tool : null,
      schemaVersion: 1,
      proposed,
    };
  }

  const schemaVersion = Number.isInteger(data.schema_version) ? data.schema_version : null;
  const cardType = findCardType(data.tool, data.schema_version);
  // `proposal` wins over the deprecated `proposed` copy; a versioned frame
  // without `proposal` does not fit the contract.
  const proposal = cardType ? parseStrictProposal(cardType.fields, data.proposal) : null;
  if (!cardType || !proposal) {
    return {
      cardKind: CARD_KIND_UNSUPPORTED,
      confirmationId,
      tool: capToolName(data.tool),
      schemaVersion,
    };
  }
  return {
    cardKind: cardType.cardKind,
    confirmationId,
    tool: cardType.tool,
    schemaVersion: cardType.schemaVersion,
    proposed: proposal,
  };
}
